import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Photo,
  Photos,
  Product,
  InfoContainer,
  Input,
  DeleteButton,
  BackButton,
  Buttons,
  Textarea,
  PhotoRelative,
  DeletePhotoContainer,
  AddPhotoIcon,
  AddPhotoContainer,
  ChevronLeft,
  BackContainer,
  DisplayedPhoto,
  AvailableTextContainer,
  AvailableText,
  LabelRow,
  Label,
  Value,
  FooterColumn,
  LinkIcon,
  FooterText,
  FooterContainer,
  EditIcon,
  LoaderContainer,
  Relative,
  DropDownValue,
  DropDown,
  StyledDropDownMenu,
  DropDownMenu,
  ChevronMenu,
  Attributes,
  PhotosWrapper,
  PhotosContainer,
  ProductWrapper,
  ProductContent,
  StyledBackContainer,
} from './ProductDetailsPage.styles.js';

import { ReactComponent as LoadingPurple } from '../../assets/icons/loader-purple.svg';
import { ReactComponent as Loading } from '../../assets/icons/loader-white.svg';

import openToast from '../../utils/toast.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import {
  deleteItem,
  editItem,
  fetchCategories,
  fetchMarketplaceCategories,
  fetchSections,
  getProductById,
  toggleItemAvailability,
} from '../../store/slices/partners/asyncThunks.js';
import {
  selectCategories,
  selectEditItemPending,
  selectGetProductByIdPending,
  selectMarketplaceCategories,
  selectSections,
  selectSelectedProduct,
  selectToggleItemAvailabilityPending,
} from '../../store/slices/partners/slice.js';
import MiniLoader from '../../components/MiniLoader/MiniLoader.js';
import ModalConfirm from '../../components/ModalConfirm/ModalConfirm.js';
import { FRONT_URL } from '../../api/index.js';
import { BUSINESS_FRONT_URL, BUSINESS_FRONT_URL_STAGING } from '../../utils/constants.js';

const ProductDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isEditing } = location.state || {};
  const [editingData, setEditingData] = useState(isEditing);
  const params = useParams();
  const id = params.id;
  const toastId = useRef(null);
  const product = useSelector(selectSelectedProduct);

  const loading = useSelector(selectGetProductByIdPending);
  const toggleItemAvailabilityPending = useSelector(selectToggleItemAvailabilityPending);
  const editItemPending = useSelector(selectEditItemPending);
  const [selectedPhoto, setSelectedPhoto] = useState({});
  const [visiblePhotos, setVisiblePhotos] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionAr, setDescriptionAr] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [price, setPrice] = useState('');
  const [availableQuantity, setAvailableQuantity] = useState('');

  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);

  const [openModal, setOpenModal] = useState(false);

  const allCategories = useSelector(selectCategories);
  const allSections = useSelector(selectSections);
  const marketplaceCategories = useSelector(selectMarketplaceCategories);
  const [openSection, setOpenSection] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [openMarketplaceCategory, setOpenMarketplaceCategory] = useState(false);

  const [webCategories, setWebCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);

  const locale = 'en';
  const ar = locale === 'ar';

  useEffect(() => {
    dispatch(getProductById({ id }))
      .unwrap()
      .then(data => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
  }, [dispatch, id]);

  const handleEditValues = useCallback(() => {
    setIsEdit(true);
    setName(product?.name);
    setDescription(product?.description);
    setDescriptionAr(product?.descriptionAr);
    setNameAr(product?.nameAr);
    setPrice(product?.price);
    setAvailableQuantity(product?.availableQuantity);
    setPhotoUrls(product?.photos);
    setWebCategories(product?.webCategories ? product?.webCategories : []);
    setCategories(product?.categories ? product?.categories : []);
    setSections(product?.sections ? product?.sections : []);
  }, [product]);

  useEffect(() => {
    if (editingData) {
      handleEditValues();
    }
  }, [handleEditValues, editingData]);

  useEffect(() => {
    dispatch(fetchCategories())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
    dispatch(fetchMarketplaceCategories())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
    dispatch(fetchSections())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
  }, [dispatch]);

  const handleCancel = () => {
    setIsEdit(false);
    setEditingData(false);
    setName('');
    setDescription('');
    setDescriptionAr('');
    setNameAr('');
    setPrice('');
    setAvailableQuantity('');
    setPhotoUrls([]);
    setPhotos([]);
    setCategories([]);
    setWebCategories([]);
    setSections([]);
  };

  const handleChange = (e, setValue) => {
    setValue(e.target.value);
  };

  const validate = () => {
    let formIsValid = true;

    if (!name || !price || !nameAr || !description || !descriptionAr || !nameAr || !availableQuantity) {
      formIsValid = false;
      openToast(toastId, 'add-item', 'warning', t('please-fill-all-inputs'));
    } else if (!photos?.length && !photoUrls?.length) {
      formIsValid = false;

      openToast(toastId, 'add-item', 'warning', t('please-upload-photos'));
    } else if (product?.approved && product?.isMobileApp && (!categories?.length || !sections?.length)) {
      formIsValid = false;

      openToast(toastId, 'add-item', 'warning', t('please-select-sections-categories'));
    } else if (product?.approved && product?.isWebApp && !webCategories?.length) {
      formIsValid = false;

      openToast(toastId, 'add-item', 'warning', t('please-select-web-categories'));
    }
    return formIsValid;
  };

  const handleEditItem = () => {
    if (validate()) {
      const requestData = {
        name,
        nameAr,
        description,
        descriptionAr,
        price,
        availableQuantity,
        photos,
        ...(photoUrls.length && { photoUrls }),
        ...(product.isMobileApp && {
          sections: sections.map(section => section.id),
        }),
        ...(product.isMobileApp && {
          categories: categories.map(category => category.id),
        }),
        ...(product.isWebApp && {
          webCategories: webCategories.map(category => category.id),
        }),
      };
      dispatch(editItem({ requestData, id }))
        .unwrap()
        .then(() => {
          openToast(toastId, 'success-create', 'success', t('you-are-successfully-edited-item'));
          handleCancel();
        })
        .catch(err => {
          openToast(toastId, 'error-create', 'error', err.message);
        });
    }
  };

  const handleDelete = () => {
    setOpenModal(true);
  };

  const handleRemoveExistingPhoto = id => {
    setPhotoUrls(prevPhotos => prevPhotos.filter(p => p.id !== id));
  };

  const handleFileUpload = event => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (photoUrls.length + imageFiles.length + photos.length <= 10) {
      setPhotos(prevPhotos => [...prevPhotos, ...imageFiles]);
    } else {
      openToast(toastId, 'warning', 'warning', t('you-can-upload-max-10-photos'));
    }
  };

  const handleRemoveFile = index => {
    setPhotos(prevPhotos => prevPhotos.filter((_, i) => i !== index));
  };

  const handleSectionClicked = section => {
    setSections([section]);
    setOpenSection(false);
  };

  const handleCategoryClicked = category => {
    setCategories([category]);
    setOpenCategory(false);
  };

  const handleMarketplaceCategoryClicked = category => {
    setWebCategories([category]);
    setOpenMarketplaceCategory(false);
  };

  const handleLinkProduct = () => {
    if (product?.available && product?.idForLink) {
      let link = FRONT_URL.includes('staging') ? BUSINESS_FRONT_URL_STAGING : BUSINESS_FRONT_URL;
      link = link + `/marketplace/${product.idForLink}`;
      window.open(link, '_blank');
    }
  };

  return (
    <MainContainer pageTitle={'product-details'} loading={loading}>
      <BackContainer onClick={() => navigate(-1)}>
        <ChevronLeft />
        {t('back')}
      </BackContainer>
      {loading ? (
        <LoaderContainer>
          <MiniLoader />
        </LoaderContainer>
      ) : !isEdit ? (
        <ProductWrapper>
          {product?.photos && product?.photos?.length > 0 && (
            <PhotosContainer>
              <PhotosWrapper>
                {product?.photos.map((photo, i) => (
                  <PhotoRelative key={i}>
                    <Photo src={photo?.url} onClick={() => setSelectedPhoto(prev => ({ ...prev, [product?.id]: photo?.url }))} />
                  </PhotoRelative>
                ))}
              </PhotosWrapper>

              <DisplayedPhoto src={selectedPhoto[product?.id] || product?.photos[0]?.url} />
            </PhotosContainer>
          )}

          <Product>
            <ProductContent>
              <InfoContainer>
                <LabelRow>
                  <Label>{t('product-name')}:</Label>
                  <Value>{product?.name}</Value>
                </LabelRow>

                <LabelRow $column>
                  <Label>{t('product-description')}:</Label>
                  <Value>{product?.description}</Value>
                </LabelRow>

                <LabelRow>
                  <Label>{t('price')}:</Label>
                  <Value>{product?.price}</Value>
                </LabelRow>
                <LabelRow>
                  <Label>{t('available-quantity')}:</Label>
                  <Value>{product?.availableQuantity}</Value>
                </LabelRow>
                {product?.skuNumber && (
                  <LabelRow>
                    <Label>{t('sku-number')}:</Label>
                    <Value>{product?.skuNumber}</Value>
                  </LabelRow>
                )}
              </InfoContainer>

              <InfoContainer>
                <LabelRow $column>
                  <Label>{t('product-name')}:</Label>
                  <Value>{product?.nameAr}</Value>
                </LabelRow>

                <LabelRow $column>
                  <Label>{t('product-description')}:</Label>
                  <Value>{product?.descriptionAr}</Value>
                </LabelRow>
              </InfoContainer>
            </ProductContent>

            <FooterContainer>
              {product?.available && product?.idForLink && (
                <FooterColumn
                  onClick={() => {
                    handleLinkProduct(product);
                  }}>
                  <LinkIcon />
                  <FooterText>{t('copy-product-link')}</FooterText>
                </FooterColumn>
              )}
              <FooterColumn
                onClick={() => {
                  handleEditValues();
                }}>
                <EditIcon />
                <FooterText>{t('edit-product-details')}</FooterText>
              </FooterColumn>
            </FooterContainer>
          </Product>
        </ProductWrapper>
      ) : (
        <ProductWrapper>
          <PhotosContainer>
            <PhotosWrapper>
              {photoUrls.length > 0 &&
                photoUrls.map((photo, i) => (
                  <PhotoRelative key={i}>
                    <Photo src={photo.url} onClick={() => setSelectedPhoto(prev => ({ ...prev, [product?.id]: photo?.url }))} />
                    <DeletePhotoContainer
                      onClick={() => {
                        handleRemoveExistingPhoto(photo.id);
                      }}>
                      x
                    </DeletePhotoContainer>
                  </PhotoRelative>
                ))}
              {photos.map((photo, i) => (
                <PhotoRelative key={i}>
                  <Photo src={URL.createObjectURL(photo)} />
                  <DeletePhotoContainer
                    onClick={() => {
                      handleRemoveFile(i);
                    }}>
                    x
                  </DeletePhotoContainer>
                </PhotoRelative>
              ))}
            </PhotosWrapper>
            <DisplayedPhoto src={selectedPhoto[product?.id] || product?.photos[0]?.url} />

            {product?.photos && product?.photos?.length > 0 && (
              <Photos>
                {photoUrls.length + photos.length < 10 && (
                  <AddPhotoContainer>
                    <input type="file" accept="image/*" multiple onChange={handleFileUpload} style={{ display: 'none' }} id="file-upload" />
                    <AddPhotoIcon onClick={() => document.getElementById('file-upload').click()} />
                  </AddPhotoContainer>
                )}
              </Photos>
            )}
          </PhotosContainer>

          <Product>
            <StyledBackContainer onClick={() => handleCancel()}>x</StyledBackContainer>
            <ProductContent>
              <InfoContainer>
                <Input
                  placeholder={t('product-name-en')}
                  value={name}
                  onChange={e => {
                    handleChange(e, setName);
                  }}
                />

                <Textarea
                  placeholder={t('product-description-en')}
                  value={description}
                  onChange={e => {
                    handleChange(e, setDescription);
                  }}
                />

                <Input
                  placeholder={t('price')}
                  value={price}
                  onChange={e => {
                    handleChange(e, setPrice);
                  }}
                />
                <Input
                  placeholder={t('available-quantity')}
                  value={availableQuantity}
                  onChange={e => {
                    handleChange(e, setAvailableQuantity);
                  }}
                />
              </InfoContainer>

              <InfoContainer>
                <Input
                  placeholder={t('product-name-ar')}
                  value={nameAr}
                  onChange={e => {
                    handleChange(e, setNameAr);
                  }}
                />

                <Textarea
                  placeholder={t('product-description-ar')}
                  value={descriptionAr}
                  onChange={e => {
                    handleChange(e, setDescriptionAr);
                  }}
                />
              </InfoContainer>

              {product?.approved && (
                <InfoContainer>
                  {product?.isMobileApp && (
                    <InfoContainer>
                      <Relative>
                        <DropDown
                          $open={openSection}
                          onClick={() => {
                            setOpenSection(!openSection);
                            setOpenCategory(false);
                            setOpenMarketplaceCategory(false);
                          }}>
                          <DropDownValue>{!sections?.length ? t('sections') : t(sections[0]?.label)}</DropDownValue>
                          <ChevronMenu $open={openSection} />
                        </DropDown>
                        {openSection && (
                          <DropDownMenu>
                            <StyledDropDownMenu>
                              {allSections.map((section, index) => (
                                <Attributes key={index} onClick={() => handleSectionClicked(section)}>
                                  {t(section.label)}
                                </Attributes>
                              ))}
                            </StyledDropDownMenu>
                          </DropDownMenu>
                        )}
                      </Relative>
                      <Relative>
                        <DropDown
                          $open={openCategory}
                          onClick={() => {
                            setOpenCategory(!openCategory);
                            setOpenMarketplaceCategory(false);
                            setOpenSection(false);
                          }}>
                          <DropDownValue>{!categories?.length ? t('category') : t(categories[0]?.name)}</DropDownValue>
                          <ChevronMenu $open={openCategory} />
                        </DropDown>
                        {openCategory && (
                          <DropDownMenu>
                            <StyledDropDownMenu>
                              {allCategories.map((category, index) => (
                                <Attributes key={index} onClick={() => handleCategoryClicked(category)}>
                                  {t(category.name)}
                                </Attributes>
                              ))}
                            </StyledDropDownMenu>
                          </DropDownMenu>
                        )}
                      </Relative>
                    </InfoContainer>
                  )}
                  {product?.isWebApp && (
                    <Relative>
                      <DropDown
                        $open={openMarketplaceCategory}
                        onClick={() => {
                          setOpenMarketplaceCategory(!openMarketplaceCategory);
                          setOpenCategory(false);
                          setOpenSection(false);
                        }}>
                        <DropDownValue>{!webCategories?.length ? t('marketplace-category') : t(webCategories[0]?.name)}</DropDownValue>
                        <ChevronMenu $open={openMarketplaceCategory} />
                      </DropDown>
                      {openMarketplaceCategory && (
                        <DropDownMenu>
                          <StyledDropDownMenu>
                            {marketplaceCategories.map((category, index) => (
                              <Attributes key={index} onClick={() => handleMarketplaceCategoryClicked(category)}>
                                {t(category.name)}
                              </Attributes>
                            ))}
                          </StyledDropDownMenu>
                        </DropDownMenu>
                      )}
                    </Relative>
                  )}
                </InfoContainer>
              )}
            </ProductContent>
            <Buttons>
              <DeleteButton
                onClick={() => {
                  handleDelete();
                }}>
                {t('delete')}
              </DeleteButton>

              {!editItemPending ? (
                <BackButton
                  onClick={() => {
                    handleEditItem();
                  }}>
                  {t('done')}
                </BackButton>
              ) : (
                <BackButton disabled>
                  <Loading height={20} />
                </BackButton>
              )}
              {!toggleItemAvailabilityPending ? (
                product?.available ? (
                  <AvailableTextContainer onClick={() => dispatch(toggleItemAvailability(product.id))}>
                    <AvailableText>✅</AvailableText>
                    <AvailableText>{t('show-hide-product')}</AvailableText>
                  </AvailableTextContainer>
                ) : (
                  <AvailableTextContainer onClick={() => dispatch(toggleItemAvailability(product.id))}>
                    <AvailableText>❌</AvailableText>
                    <AvailableText>{t('show-hide-product')}</AvailableText>
                  </AvailableTextContainer>
                )
              ) : (
                <AvailableTextContainer style={{ cursor: 'pointer' }}>
                  <LoadingPurple height={30} width={30} />
                  <AvailableText style={{ opacity: 0, userSelect: 'none' }}>{t('show-hide-product')}</AvailableText>
                </AvailableTextContainer>
              )}
            </Buttons>
          </Product>
        </ProductWrapper>
      )}
      <ModalConfirm
        open={openModal}
        onClickNo={() => {
          setOpenModal(false);
        }}
        onClickYes={() => {
          dispatch(deleteItem(id))
            .unwrap()
            .then(() => {
              openToast(toastId, 'success-create', 'success', t('you-are-successfully-deleted-item'));
              navigate(-1);
              setOpenModal(false);
            })
            .catch(err => {
              openToast(toastId, 'error-create', 'error', err.message);
            });
        }}
      />
    </MainContainer>
  );
};

export default ProductDetailsPage;
